/* ===================================================================
   ReactivationStep -- the first-run "Rhody Reviews Reactivation" step.
   Brad, 2026-07-06 (coordinator brief + Solove compliance memo
   2026-07-06_cc-trial-and-reactivation-compliance.md).

   WHAT IT DOES: invites the client to upload a CSV of PAST customers to
   re-engage for fresh reviews. Presented OPT-OUT (default = recommended /
   enrolled), but NOTHING is sent without the client uploading a list AND
   ticking the consent attestation. The upload is HARD-BLOCKED until the
   attestation checkbox is ticked; the server independently 403s any
   enroll without consent_attested:true.

   CADENCE: 2 texts + 1 email. The TWO TEXTS are SYSTEM-GATED OFF until
   Twilio A2P/10DLC is approved -- shown as "unlocks when your texting
   number is live". Email runs now. The server reports the real gate
   (channel_scope / sms_enabled); this UI reflects it honestly, never
   promising a text that cannot send.

   TCPA SAFETY: the attestation checkbox (Solove's FINAL wording, verbatim
   in ATTESTATION_TEXT) is the gate. No score-gating -- the ask goes to
   EVERYONE uploaded (Hard Rule #4).

   Rhody Reviews is the actor in all copy (never "I"/"we"/"Steve").
   ASCII-safe.
   =================================================================== */
import { useRef, useState } from 'react';
import { useReactivationEnroll } from '../hooks';
import { parseCustomersCsv } from '../lib/csv';
import type { CustomerInput } from '../lib/api';

/* ---------------------------------------------------------------------
   ATTESTATION_TEXT -- Solove's FINAL, legally-binding wording (2026-07-06).
   VERBATIM. Do not paraphrase. Any change is a one-line swap here; bump
   ATTESTATION_TEXT_VERSION when the wording changes so the audit ledger
   can distinguish versions.
   --------------------------------------------------------------------- */
export const ATTESTATION_TEXT =
  'By uploading this list, I confirm that each customer on it is a past or ' +
  'current customer of my business with whom I have an established business ' +
  'relationship, that I collected their phone number and email directly from ' +
  'them in the course of doing business, and that I have their consent (or a ' +
  'lawful basis) to contact them with a review request at the number and ' +
  'email I am providing. I understand Rhody Reviews sends these messages on ' +
  'my behalf, as my business, and that I am responsible for the accuracy of ' +
  'this list and this consent. I will not upload purchased, rented, scraped, ' +
  'or third-party lists.';

export const ATTESTATION_TEXT_VERSION = '2026-07-06';

/* Money stat -- attributed, not overclaimed. Source: Harvard Business School
   research (Michael Luca). Kept conservative ("roughly", a range). */
const REVENUE_STAT =
  'Harvard Business School research (Michael Luca) found that a one-star ' +
  'increase in a business online rating drives roughly a 5 to 9 percent ' +
  'increase in revenue.';

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} width={16} height={16}>
      <path d="M5 12l5 5L20 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={14} height={14}>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" strokeLinecap="round" />
    </svg>
  );
}

export interface ReactivationStepProps {
  /* Called once an enrollment actually succeeds (drives the honest done
     screen). Passes the number of contacts enrolled. */
  onEnrolled?: (count: number) => void;
  /* Called when the client actively opts out of the playbook. */
  onOptOut?: (optedOut: boolean) => void;
}

export function ReactivationStep({ onEnrolled, onOptOut }: ReactivationStepProps) {
  const { enroll, isPending } = useReactivationEnroll();
  const fileRef = useRef<HTMLInputElement>(null);

  // OPT-OUT model: default enrolled (opted in). The client must actively skip.
  const [optedOut, setOptedOut] = useState(false);
  const [attested, setAttested] = useState(false);
  const [parsed, setParsed] = useState<{ rows: CustomerInput[]; total: number; fileName: string } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [enrolledCount, setEnrolledCount] = useState<number | null>(null);
  const [smsEnabled, setSmsEnabled] = useState(false);

  function pickFile() {
    setMsg(null);
    fileRef.current?.click();
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setMsg('Reading file...');
    try {
      const text = await file.text();
      const res = parseCustomersCsv(text);
      if (res.rows.length === 0) {
        setParsed(null);
        setMsg('No usable rows found. The CSV needs a name plus an email or phone column.');
        return;
      }
      setParsed({ rows: res.rows, total: res.totalRows, fileName: file.name });
      const dropped = res.totalRows - res.rows.length;
      setMsg(
        `Found ${res.rows.length} contact${res.rows.length === 1 ? '' : 's'} in ${file.name}` +
          (dropped > 0 ? ` (${dropped} row${dropped === 1 ? '' : 's'} skipped: no email/phone).` : '.'),
      );
    } catch (err) {
      setParsed(null);
      setMsg(`Could not read that file${err instanceof Error ? ` (${err.message})` : ''}.`);
    }
  }

  // HARD BLOCK: enroll is disabled until a list is loaded AND the attestation
  // is ticked. The server also 403s an unattested enroll (defense in depth).
  const canEnroll = !!parsed && attested && !isPending && enrolledCount === null;

  async function handleEnroll() {
    if (!parsed || !attested) return;
    setMsg(null);
    try {
      const res = await enroll({
        customers: parsed.rows,
        consent_attested: true,
        attestation_text: ATTESTATION_TEXT,
        attestation_text_version: ATTESTATION_TEXT_VERSION,
      });
      setEnrolledCount(res.enrolled);
      setSmsEnabled(res.sms_enabled);
      onEnrolled?.(res.enrolled);
      setMsg(null);
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      setMsg(
        detail.includes('consent')
          ? 'Please tick the confirmation checkbox before uploading.'
          : 'Something went wrong enrolling your list. Please try again in a moment.',
      );
    }
  }

  // ---- OPTED OUT state ----
  if (optedOut) {
    return (
      <>
        <div className="ey">Step 4 &middot; Reactivation</div>
        <h2>Reactivation skipped for now</h2>
        <p className="desc">
          No problem. You can turn on the Reactivation playbook anytime from the
          Customers tab. Nothing has been sent.
        </p>
        <button
          className="btn btn--ghost"
          style={{ marginTop: 8 }}
          onClick={() => {
            setOptedOut(false);
            onOptOut?.(false);
          }}
        >
          Actually, let&rsquo;s set it up
        </button>
      </>
    );
  }

  // ---- ENROLLED (success) state ----
  if (enrolledCount !== null) {
    return (
      <>
        <div className="ey">Step 4 &middot; Reactivation</div>
        <h2>Your past customers are enrolled</h2>
        <p className="desc">
          {enrolledCount} past customer{enrolledCount === 1 ? '' : 's'} enrolled in the
          Reactivation playbook. The email ask goes out on your cadence.
          {smsEnabled ? '' : ' The two text touches unlock automatically once your texting number is live.'}
        </p>
        <div className="react-note react-note--ok">
          <CheckIcon /> Nothing was sent yet. Rhody Reviews sends on your behalf,
          as your business, and every message includes an easy opt-out.
        </div>
      </>
    );
  }

  // ---- MAIN (opted-in) state ----
  return (
    <>
      <div className="ey">Step 4 &middot; Reactivation</div>
      <h2>Turn past customers into fresh reviews</h2>
      <p className="desc">
        Most of your best reviews are still sitting in your old customer list.
        The Reactivation playbook asks them, for you, to leave a fresh review.
      </p>

      {/* WHY it matters -- attributed money stat, no overclaiming. */}
      <div className="react-stat">
        <div className="react-stat__big">Why it&rsquo;s worth it</div>
        <p>{REVENUE_STAT}</p>
        <span className="react-stat__src">Source: Harvard Business School research</span>
      </div>

      {/* HOW it works -- the 3-touch cadence, honest about A2P. */}
      <div className="react-cadence">
        <div className="react-cadence__title">How the ask works: 3 gentle touches</div>
        <ul className="react-cadence__list">
          <li>
            <span className="react-chip react-chip--soon"><LockIcon /> Text 1</span>
            <span>A short, friendly text. Unlocks when your texting number is live.</span>
          </li>
          <li>
            <span className="react-chip react-chip--live">Email</span>
            <span>A personal email ask. This runs now.</span>
          </li>
          <li>
            <span className="react-chip react-chip--soon"><LockIcon /> Text 2</span>
            <span>One last gentle nudge. Unlocks when your texting number is live.</span>
          </li>
        </ul>
        <p className="react-cadence__note">
          Texting turns on automatically once your carrier registration (A2P) is
          approved. Until then it&rsquo;s email-first, so you start collecting reviews right away.
        </p>
      </div>

      {/* UPLOAD -- with the inline "own customers only" note above the button. */}
      <div className="react-upload">
        <p className="react-upload__warn">
          Only upload your own past customers. Never a purchased or scraped list.
        </p>
        <button className="btn btn--ghost" onClick={pickFile} disabled={isPending}>
          {parsed ? 'Choose a different file' : 'Upload past-customer CSV'}
        </button>
        {msg && <div className="react-note">{msg}</div>}
      </div>

      {/* ATTESTATION -- hard gate. Unchecked by default; upload blocked until ticked. */}
      <label className="react-attest">
        <input
          type="checkbox"
          checked={attested}
          onChange={(e) => setAttested(e.target.checked)}
        />
        <span>{ATTESTATION_TEXT}</span>
      </label>

      <div className="react-actions">
        <button
          className="btn btn--primary"
          onClick={handleEnroll}
          disabled={!canEnroll}
          style={!canEnroll ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
        >
          {isPending ? 'Enrolling...' : 'Enroll my past customers'}
        </button>
        <button
          className="btn btn--text"
          onClick={() => {
            setOptedOut(true);
            onOptOut?.(true);
          }}
          disabled={isPending}
        >
          Skip for now
        </button>
      </div>
      {!parsed && (
        <p className="react-hint">Upload a CSV to continue, or skip for now.</p>
      )}
      {parsed && !attested && (
        <p className="react-hint">Tick the confirmation above to enable the button.</p>
      )}

      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        onChange={onFile}
      />
    </>
  );
}

export default ReactivationStep;
